/**
 * Two-finger pinch and pan for touch screens, shared by the editor and the simulator.
 * One finger is left to the normal pointer handling (select, move, draw). When a second finger
 * lands the gesture takes over, and the remaining pointer events are ignored until every finger
 * has lifted, so a finger left behind does not start a drag. Mouse and pen are never affected.
 */
import type { Viewport } from '../../state/store';
import { pinchViewport } from './zoom';

interface Pt {
  x: number;
  y: number;
}

const mid = (a: Pt, b: Pt): Pt => ({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 });
const gap = (a: Pt, b: Pt): number => Math.hypot(a.x - b.x, a.y - b.y);

export class TouchGesture {
  private points = new Map<number, Pt>();
  private start: { mid: Pt; dist: number; view: Viewport } | null = null;
  /** True from the moment a second finger lands until all fingers are lifted. */
  active = false;

  /** Returns true when the event starts (or belongs to) a pinch and must not be handled further. */
  down(e: { pointerId: number; pointerType: string; clientX: number; clientY: number }, origin: Pt, view: Viewport): boolean {
    if (e.pointerType !== 'touch') return false;
    this.points.set(e.pointerId, { x: e.clientX - origin.x, y: e.clientY - origin.y });
    if (this.points.size === 2) {
      const [a, b] = [...this.points.values()];
      this.start = { mid: mid(a, b), dist: gap(a, b), view };
      this.active = true;
    }
    return this.active;
  }

  /** The new viewport while pinching, or null when the event is not part of a pinch. */
  move(e: { pointerId: number; pointerType: string; clientX: number; clientY: number }, origin: Pt): Viewport | null {
    if (e.pointerType !== 'touch' || !this.points.has(e.pointerId)) return null;
    this.points.set(e.pointerId, { x: e.clientX - origin.x, y: e.clientY - origin.y });
    if (!this.start || this.points.size < 2) return null;
    const [a, b] = [...this.points.values()];
    return pinchViewport(this.start.view, this.start.mid, this.start.dist, mid(a, b), gap(a, b));
  }

  /** Returns true when the event belonged to a pinch (also the last finger lifting after one). */
  up(e: { pointerId: number; pointerType: string }): boolean {
    if (e.pointerType !== 'touch') return false;
    this.points.delete(e.pointerId);
    const wasActive = this.active;
    if (this.points.size < 2) this.start = null;
    if (this.points.size === 0) this.active = false;
    return wasActive;
  }
}
